import React, { useCallback, useMemo, useState, useEffect } from "react";
import { toast, Toaster } from "react-hot-toast";
import { Database, Pencil, Trash2 } from "lucide-react";
// import DatabricksIcon from "../../../assests/databricks.png"
// import AzureDatabricksIcon from "../../../assests/azure-databricks.png"
import AdlsIcon from "../../../assests/ADLS-SVG.svg";
import DatabricksSVG from "../../../assests/Databricks.svg";
import AzureDatabricksSVG from "../../../assests/Azure.svg";
import PowerBIIcon from "../../../assests/power-bi-icon.svg";
// import dummyData from "./DummyData";
import loader from "../../../assests/Loader5.gif";
import customLineageService from '../../../services/customLineageService';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  applyEdgeChanges,
  applyNodeChanges,
  BackgroundVariant,
  ReactFlowProvider
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./ViewLineage.css";

const toSafeGraphId = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z0-9_.-]/g, "-")
    .slice(0, 100);

const mapApiResponseToLineageGraph = (apiResponse) => {
  const nodes = [];
  const edges = [];
  const nodeIdMap = new Map(); // To track node IDs and avoid duplicates
  let nodeCounter = 1;

  // Helper function to generate unique node ID
  const generateNodeId = (tableName) => {
    if (nodeIdMap.has(tableName)) {
      return nodeIdMap.get(tableName);
    }
    const id = nodeCounter.toString();
    nodeIdMap.set(tableName, id);
    nodeCounter++;
    return id;
  };

  // Helper function to extract columns data for TableNode
  const extractColumnsData = (columns) => {
    // Handle null, undefined, non-arrays, or empty arrays
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      return { visibleColumns: [], allColumns: [], showMoreText: null };
    }

    // Handle both old format (objects with name and type) and new format (just strings)
    const allColumns = columns.map((col) => {
      if (typeof col === 'string') {
        // New format: columns are just strings
        return {
          name: col,
          type: "string", // Default type since API doesn't provide it
        };
      } else {
        // Old format: columns are objects with name and type
        return {
      name: col.name || col.column_name || "",
      type: col.type || col.column_type || col.type_name || col.type_text || "unknown",
      description: col.description || col.column_description || "",
        };
      }
    });

    // Show first 6 columns by default, rest in "show more"
    const visibleColumns = allColumns.slice(0, 3);
    const remainingCount = allColumns.length - 3;
    const showMoreText =
      remainingCount > 0 ? `Show ${remainingCount} more columns` : null;

    return { visibleColumns, allColumns, showMoreText };
  };

  // Helper function to create a node from metadata
  const createNodeFromMetadata = (
    metadata,
    tableName,
    position = { x: 0, y: 0 },
    isPrimaryNode = false,
    columns = null // New parameter for columns array
  ) => {
    const nodeId = generateNodeId(tableName);
    
    // Use columns parameter first, then fall back to metadata.columns
    const columnsToUse = columns || (metadata && metadata.columns) || [];
    
    const { visibleColumns, allColumns, showMoreText } = extractColumnsData(columnsToUse);
    
    // If no metadata and no columns, provide default information
    if (!metadata && (!columns || columns.length === 0)) {
    return {
      id: nodeId,
      type: "tableNode",
      position,
      data: {
        tableName: tableName,
          owner: "Unknown",
          description: "",
          schemaName: "",
          databaseName: "",
          sourceSystem: "",
          visibleColumns: [],
          allColumns: [],
          showMoreText: null,
          isPrimaryNode: isPrimaryNode,
        },
      };
    }

    return {
      id: nodeId,
      type: "tableNode",
      position,
      data: {
        tableName: tableName,
        owner: (metadata && metadata.owner) || "Unknown",
        description: (metadata && metadata.description) || "",
        schemaName: (metadata && metadata.schema_name) || "",
        databaseName: (metadata && metadata.database_name) || "",
        sourceSystem: (metadata && metadata.source_system) || "",
        visibleColumns,
        allColumns,
        showMoreText,
        isPrimaryNode: isPrimaryNode,
      },
    };
  };

  // Helper function to process upstreams recursively
  const processUpstreams = (upstreams, parentNodeId, level = 0) => {
    if (!upstreams || !Array.isArray(upstreams)) return;

    // Get parent node to calculate proper positioning
    const parentNode = nodes.find(n => n.id === parentNodeId);
    const parentY = parentNode ? parentNode.position.y : 700;
    
    // Calculate spacing based on number of upstreams
    const totalUpstreams = upstreams.length;
    const spacing = totalUpstreams === 1 ? 0 : 250; // No spacing for single child, 250px for multiple
    const startY = totalUpstreams === 1 ? parentY : parentY - (totalUpstreams - 1) * spacing / 2;

    upstreams.forEach((upstream, index) => {
      const childTableName = upstream.child;
      
      // Skip if no table name is available
      if (!childTableName) return;
      
      const nodeId = generateNodeId(childTableName);

      // Track custom nodes so we can restore nodeToFullName on reload
      if (upstream.source === 'custom') {
        customTableMap[nodeId] = childTableName;
      }

      // Create node if it doesn't exist (regardless of metadata)
      if (!nodes.find((n) => n.id === nodeId)) {
        const position = {
          x: Math.max(0, 500 - (level + 1) * 600), // Increased horizontal spacing
          y: startY + index * spacing,
        };
        const node = createNodeFromMetadata(
          upstream.metadata,
          childTableName,
          position,
          false, // isPrimaryNode
          upstream.columns // Pass columns from new API structure
        );
        if (upstream.source === 'custom') {
          node.data.isCustom = true;
        }
        nodes.push(node);
      }

      // Create edge from child to parent (always create edge if both nodes exist)
      const edgeId = `e${nodeId}-${parentNodeId}`;
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: nodeId,
          target: parentNodeId,
          type: "bezier",
          animated: false,
          style: {
            stroke: '#374151',
            strokeWidth: 3,
          },
          markerEnd: {
            type: 'arrowclosed',
            color: '#374151',
            width: 20,
            height: 20
          },
        });
      }

      // Process parents recursively
      if (upstream.parents && upstream.parents.length > 0) {
        processUpstreams(upstream.parents, nodeId, level + 1);
      }
    });
  };

  // Helper function to process downstreams recursively
  const processDownstreams = (downstreams, parentNodeId, level = 0) => {
    if (!downstreams || !Array.isArray(downstreams)) return;

    // Get parent node to calculate proper positioning
    const parentNode = nodes.find(n => n.id === parentNodeId);
    const parentY = parentNode ? parentNode.position.y : 700;
    
    // Calculate spacing based on number of downstreams
    const totalDownstreams = downstreams.length;
    const spacing = totalDownstreams === 1 ? 0 : 250; // No spacing for single child, 250px for multiple
    const startY = totalDownstreams === 1 ? parentY : parentY - (totalDownstreams - 1) * spacing / 2;

    downstreams.forEach((downstream, index) => {
      const childTableName = downstream.parent || downstream.child;
      
      // Skip if no table name is available
      if (!childTableName) return;
      
      const nodeId = generateNodeId(childTableName);

      // Track custom nodes so we can restore nodeToFullName on reload
      if (downstream.source === 'custom') {
        customTableMap[nodeId] = childTableName;
      }

      // Create node if it doesn't exist (regardless of metadata)
      if (!nodes.find((n) => n.id === nodeId)) {
        const position = {
          x: 500 + (level + 1) * 600, // Increased horizontal spacing
          y: startY + index * spacing,
        };
        const node = createNodeFromMetadata(
          downstream.metadata,
          childTableName,
          position,
          false, // isPrimaryNode
          downstream.columns // Pass columns from new API structure
        );
        if (downstream.source === 'custom') {
          node.data.isCustom = true;
        }
        nodes.push(node);
      }

      // Create edge from parent to child (always create edge if both nodes exist)
      const edgeId = `e${parentNodeId}-${nodeId}`;
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: parentNodeId,
          target: nodeId,
          type: "bezier",
          animated: false,
          style: {
            stroke: '#374151',
            strokeWidth: 3,
          },
          markerEnd: {
            type: 'arrowclosed',
            color: '#374151',
            width: 20,
            height: 20
          },
        });
      }

      // Process children recursively
      if (downstream.children && downstream.children.length > 0) {
        processDownstreams(downstream.children, nodeId, level + 1);
      }
    });
  };

  // Map of nodeId -> full_name for custom tables (rebuilt on each load)
  const customTableMap = {};

  // Process main element
  const mainTableName = apiResponse.name;
  const mainNodeId = generateNodeId(mainTableName);

  // Create main node (primary node)
  const mainNode = createNodeFromMetadata(
    apiResponse.metadata, 
    mainTableName, 
    { x: 500, y: 700 }, 
    true, // Mark as primary node
    apiResponse.columns // Pass columns from new API structure
  );
  nodes.push(mainNode);

  // Process upstreams
  if (apiResponse.upstreams) {
    processUpstreams(apiResponse.upstreams, mainNodeId);
  }

  // Process downstreams
  if (apiResponse.downstreams) {
    processDownstreams(apiResponse.downstreams, mainNodeId);
  }

  return { nodes, edges, customTableMap };
};

// Table Details Modal Component
const TableDetailsModal = ({ isOpen, onClose, onSubmit, editingNode, nodes, mode }) => {
  const [formData, setFormData] = useState({
    tableName: '',
    schemaName: '',
    databaseName: '',
    sourceSystem: '',
    description: '',
    columns: [{ name: '', dataType: '', description: '' }]
  });

  // Update form data based on mode and selected node
  React.useEffect(() => {
    if (mode === 'edit' && editingNode) {
      const node = nodes.find(n => n.id === editingNode);
      if (node) {
        setFormData({
          tableName: node.data.tableName || '',
          schemaName: node.data.schemaName || '',
          databaseName: node.data.databaseName || '',
          sourceSystem: node.data.sourceSystem || '',
          description: node.data.description || '',
          columns: node.data.allColumns?.map(col => ({
            name: col.name || '',
            dataType: col.type || '',
            description: col.description || ''
          })) || [{ name: '', dataType: '', description: '' }]
        });
      }
    } else {
      setFormData({
        tableName: '',
        schemaName: '',
        databaseName: '',
        sourceSystem: '',
        description: '',
        columns: [{ name: '', dataType: '', description: '' }]
      });
    }
  }, [editingNode, nodes, mode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => 
        i === index ? { ...col, [field]: value } : col
      )
    }));
  };

  const addColumn = () => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { name: '', dataType: '', description: '' }]
    }));
  };

  const removeColumn = (index) => {
    if (formData.columns.length > 1) {
      setFormData(prev => ({
        ...prev,
        columns: prev.columns.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="viewlineage-modal-overlay" onClick={onClose}>
      <div className="viewlineage-table-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="viewlineage-modal-header">
          <h3>{mode === 'edit' ? 'Edit Table' : 'Add New Table'}</h3>
          <button className="viewlineage-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="viewlineage-modal-form">
          <div className="viewlineage-form-group">
            <label htmlFor="tableName">Table Name *</label>
            <input
              type="text"
              id="tableName"
              value={formData.tableName}
              onChange={(e) => handleInputChange('tableName', e.target.value)}
              required
              placeholder="Enter table name"
            />
          </div>

          <div className="viewlineage-form-group">
            <label htmlFor="schemaName">Schema Name *</label>
            <input
              type="text"
              id="schemaName"
              value={formData.schemaName}
              onChange={(e) => handleInputChange('schemaName', e.target.value)}
              required
              placeholder="Enter schema name"
            />
          </div>

          <div className="viewlineage-form-group">
            <label htmlFor="databaseName">Database Name</label>
            <input
              type="text"
              id="databaseName"
              value={formData.databaseName}
              onChange={(e) => handleInputChange('databaseName', e.target.value)}
              placeholder="Enter database/catalog name"
            />
          </div>

          <div className="viewlineage-form-group">
            <label htmlFor="sourceSystem">Source System</label>
            <input
              type="text"
              id="sourceSystem"
              value={formData.sourceSystem}
              onChange={(e) => handleInputChange('sourceSystem', e.target.value)}
              placeholder="e.g. databricks, sqlserver"
            />
          </div>

          <div className="viewlineage-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter table description"
              rows="3"
            />
          </div>

          <div className="viewlineage-form-group">
            <div className="viewlineage-columns-header">
              <label>Columns</label>
              <button type="button" onClick={addColumn} className="viewlineage-add-column-btn">
                + Add Column
              </button>
            </div>
            
            <div className="viewlineage-columns-list">
              {formData.columns.map((column, index) => (
                <div key={index} className="viewlineage-column-item">
                  <div className="viewlineage-column-inputs">
                    <input
                      type="text"
                      placeholder="Column name"
                      value={column.name}
                      onChange={(e) => handleColumnChange(index, 'name', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Data type"
                      value={column.dataType}
                      onChange={(e) => handleColumnChange(index, 'dataType', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={column.description}
                      onChange={(e) => handleColumnChange(index, 'description', e.target.value)}
                    />
                  </div>
                  {formData.columns.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeColumn(index)}
                      className="viewlineage-remove-column-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="viewlineage-modal-actions">
            <button type="button" onClick={onClose} className="viewlineage-cancel-btn">
              Cancel
            </button>
            <button type="submit" className="viewlineage-save-btn">
              {mode === 'edit' ? 'Update Table' : 'Add Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Custom Table Node Component
const TableNode = ({ data, selected, id }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { tableName, visibleColumns, showMoreText, allColumns, owner, description, isEmpty, isPrimaryNode, isCustom, onExpandNode, onEditNode, onDeleteNode, isExpanding, isDeleting, onColumnLineage, isColumnLineageLoading, hasColumnLineage, connectedColumns, onColumnClick, highlightedColumn } = data;

  // Determine which columns to show based on expansion state or column lineage
  // Auto-expand when column lineage is active to show all column handles
  const shouldShowAllColumns = isExpanded || hasColumnLineage;
  const displayColumns = shouldShowAllColumns ? allColumns : visibleColumns;
  const toggleText = isExpanded ? "Hide columns" : showMoreText;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleExpandClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onExpandNode) {
      onExpandNode(id, tableName);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onDeleteNode) {
      onDeleteNode(id);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onEditNode) {
      onEditNode(id);
    }
  };

  const handleColumnLineageClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onColumnLineage) {
      onColumnLineage(id, tableName);
    }
  };

  const handleColumnClick = (e, columnName) => {
    e.stopPropagation(); // Prevent node selection
    if (onColumnClick && hasColumnLineage) {
      onColumnClick(id, columnName, tableName);
    }
  };

  const normalizedName = (tableName || '').toLowerCase();
  const isAdlsSource = normalizedName.includes('abfss://');

  const tableIcon = useMemo(() => {
    if (isCustom) {
      return {
        variant: 'single',
        content: (
          <div className="table-icon table-icon-custom">
            <Database className="table-icon-lucide" aria-hidden="true" />
          </div>
        ),
      };
    }

    if (isAdlsSource) {
      return {
        variant: 'single',
        content: (
          <div className="table-icon-dual-inner">

            <img
              src={AdlsIcon}
              alt="ADLS icon"
              className="table-icon-img"
            />
          </div>
        ),
      };
    }

    return {
      variant: 'dual',
      content: (
        <div className="table-icon-dual-inner">
          <img
            src={AzureDatabricksSVG}
            alt="Azure Databricks"
            className="table-icon-dual-img"
          />
          <img
            src={DatabricksSVG}
            alt="Databricks"
            className="table-icon-dual-img"
          />
        </div>
      ),
    };
  }, [isAdlsSource, isCustom]);

  return (
    <div 
      className={`table-node ${selected ? "table-node-selected" : ""} ${isPrimaryNode ? "table-node-primary" : ""} ${isCustom ? "table-node-custom" : ""}`}
      data-is-empty={isEmpty ? "true" : "false"}
      data-is-primary={isPrimaryNode ? "true" : "false"}
      data-has-column-lineage={hasColumnLineage ? "true" : "false"}
    >
      {/* Table Header */}
      <div className="table-header">
          {tableIcon.content}
      
        <div className="table-info">
          <div className="table-label-row">
            <div className="table-label">{isAdlsSource ? "File" : "Table"}</div>
            {isCustom && <span className="custom-node-badge">Custom</span>}
          </div>
          <div className="table-name" title={tableName}>
            {tableName}
          </div>
          {description && (
            <div className="table-description" title={description}>
              {description}
            </div>
          )}
          
        </div>
        {!isEmpty && (
          <div 
            className={`table-action ${isExpanding ? 'table-action-loading' : ''}`}
            onClick={handleExpandClick} 
            title={isExpanding ? "Loading..." : "Expand node to load children/parents"}
          >
            {isExpanding ? (
              <div className="expand-spinner"></div>
            ) : (
          <svg className="action-icon" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            ></path>
          </svg>
            )}
          </div>
        )}
        {!isEmpty && isCustom && (
          <div
            className="table-action"
            onClick={handleEditClick}
            title="Edit custom table"
            style={{ color: '#0f766e' }}
          >
            <Pencil className="action-icon" strokeWidth={2} />
          </div>
        )}
        {!isEmpty && isCustom && (
          <div
            className={`table-action ${isDeleting ? 'table-action-loading' : ''}`}
            onClick={handleDeleteClick}
            title={isDeleting ? 'Deleting...' : 'Delete custom table'}
            style={{ color: '#ef4444' }}
          >
            {isDeleting ? (
              <div className="expand-spinner"></div>
            ) : (
              <Trash2 className="action-icon" strokeWidth={2} />
            )}
          </div>
        )}
      </div>

      {/* Table Columns */}
      <div className="table-columns">
        {displayColumns.map((column, index) => {
          const isConnected = connectedColumns && connectedColumns.includes(column.name);
          // Check if this column is highlighted (clicked in this node) OR if it's the related column in another node
          const isDirectlyHighlighted = highlightedColumn && highlightedColumn.nodeId === id && highlightedColumn.columnName === column.name;
          const isRelatedHighlighted = highlightedColumn && highlightedColumn.nodeId !== id && 
                                       highlightedColumn.relatedColumns && 
                                       highlightedColumn.relatedColumns.some(rc => rc.nodeId === id && rc.columnName === column.name);
          const isHighlighted = isDirectlyHighlighted || isRelatedHighlighted;
          
          return (
            <div 
              key={index} 
              className={`column-row ${isConnected ? 'column-connected' : ''} ${isHighlighted ? 'column-highlighted' : ''} ${hasColumnLineage ? 'column-clickable' : ''}`} 
              style={{ position: 'relative' }}
              onClick={(e) => handleColumnClick(e, column.name)}
            >
            <span className="column-name">{column.name}</span>
            <span className="column-type">{column.type}</span>
              {/* Column-specific handles for lineage connections */}
              <Handle
                type="target"
                position={Position.Left}
                id={`col-${column.name}`}
                className={`column-handle column-handle-left ${isConnected ? 'column-handle-connected' : ''} ${isHighlighted ? 'column-handle-highlighted' : ''}`}
                style={{ 
                  left: -6, 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px'
                }}
              />
              <Handle
                type="source"
                position={Position.Right}
                id={`col-${column.name}`}
                className={`column-handle column-handle-right ${isConnected ? 'column-handle-connected' : ''} ${isHighlighted ? 'column-handle-highlighted' : ''}`}
                style={{ 
                  right: -6, 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px'
                }}
              />
          </div>
          );
        })}

        {showMoreText && !hasColumnLineage && (
          <div className="show-more-container">
            <button className="show-more-btn" onClick={handleToggle}>
              {toggleText}
            </button>
          </div>
        )}
      </div>

      {/* Column Lineage Button - Only show if node has columns */}
      {!isEmpty && allColumns && allColumns.length > 0 && (
        <div className="column-lineage-button-container">
          <button 
            className={`column-lineage-btn ${hasColumnLineage ? 'active' : ''} ${isColumnLineageLoading ? 'loading' : ''}`}
            onClick={handleColumnLineageClick}
            disabled={isColumnLineageLoading}
            title={hasColumnLineage ? "Hide Column Lineage" : "Show Column Lineage"}
          >
            {isColumnLineageLoading ? (
              <>
                <div className="column-lineage-spinner"></div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg className="column-lineage-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 112 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 110 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 110-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd"></path>
                </svg>
                <span>{hasColumnLineage ? "Hide Column Lineage" : "Column Lineage"}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Empty node indicator */}
      {isEmpty && (
        <div className="empty-node-indicator">
          <div className="empty-node-text">Click to configure</div>
        </div>
      )}

      {/* Connection Handles - Positioned at header level with custom IDs */}
      <Handle
        type="target"
        position={Position.Left}
        id="header-left"
        className="connection-handle header-handle"
        data-handlepos="left"
        style={{
          top: '25px !important', // Position at header level
          left: '-10px !important',
          transform: 'translateY(-50%) !important',
          position: 'absolute !important',
          zIndex: 10
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="header-right"
        className="connection-handle header-handle"
        data-handlepos="right"
        style={{
          top: '25px !important', // Position at header level
          right: '-10px !important',
          transform: 'translateY(-50%) !important',
          position: 'absolute !important',
          zIndex: 10
        }}
      />
    </div>
  );
};

const ReportNode = ({ data, selected }) => {
  return (
    <div className={`report-asset-node ${selected ? "report-asset-node-selected" : ""}`} title={data.label}>
      <div className="report-asset-header">
        <div className="report-asset-icon">
          <img src={PowerBIIcon} alt="Power BI" className="report-asset-icon-svg" />
        </div>
        <div className="report-asset-info">
          <div className="report-asset-label">Power BI Report</div>
          <div className="report-asset-name">{data.label}</div>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Left}
        id="header-left"
        className="connection-handle header-handle"
        data-handlepos="left"
        style={{
          top: "25px",
          left: "-10px",
          transform: "translateY(-50%)",
          position: "absolute",
          zIndex: 10,
        }}
      />
    </div>
  );
};

const nodeTypes = {
  tableNode: TableNode,
  reportNode: ReportNode,
};

const ViewLineage = ({ contractData }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingNode, setEditingNode] = useState(null);
  const [nodeCounter, setNodeCounter] = useState(1000); // Start from 1000 to avoid conflicts with API nodes
  const [expandingNodeId, setExpandingNodeId] = useState(null); // Track which node is being expanded
  const [columnLineageLoadingNodeId, setColumnLineageLoadingNodeId] = useState(null); // Track column lineage loading
  const [columnLineageData, setColumnLineageData] = useState({}); // Store column lineage data by node ID
  const [columnLineageEdges, setColumnLineageEdges] = useState([]); // Store column lineage edges
  const [highlightedColumn, setHighlightedColumn] = useState(null); // Track highlighted column {nodeId, columnName, tableName}
  const [reportsByTable, setReportsByTable] = useState({});
  
  // Custom lineage state
  const [savingTableNodeId, setSavingTableNodeId] = useState(null); // Track which table is being saved
  const [savingEdgeId, setSavingEdgeId] = useState(null); // Track which edge is being saved
  const [nodeToFullName, setNodeToFullName] = useState({}); // Map node ID to full_name for custom tables
  const [edgeToLineageId, setEdgeToLineageId] = useState({}); // Map edge ID to lineage_id for custom edges

  const contractName = contractData ? contractData.name.replace('|', '.') : '';
  const filePath = contractData ? contractData.filePath : '';
  const sourceType = contractData ? contractData.source_type : '';

  const API_URL = `${process.env.REACT_APP_API_URL}/lineage?table_name=${filePath}&source_type=${sourceType}`;

  // Fetch data from API
  const fetchLineageData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setApiData(data);
    } catch (err) {
      console.error('Failed to get access token:', err.response?.data || err.message);
      setError(err.message);
      // Fallback to dummy data
      //   setApiData(dummyData);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch data on component mount
  useEffect(() => {
    fetchLineageData();
  }, [fetchLineageData]);



  // Generate nodes and edges from API data
  const { nodes: generatedNodes, edges: generatedEdges, customTableMap: generatedCustomMap } = useMemo(() => {
    if (apiData) {
      return mapApiResponseToLineageGraph(apiData);
    }
    return { nodes: [], edges: [], customTableMap: {} };
  }, [apiData]);

  // Restore nodeToFullName for custom nodes loaded from API (enables edit/delete after reload)
  useEffect(() => {
    if (generatedCustomMap && Object.keys(generatedCustomMap).length > 0) {
      setNodeToFullName(prev => ({ ...prev, ...generatedCustomMap }));
    }
  }, [generatedCustomMap]);

  const [nodes, setNodes] = useNodesState(generatedNodes);
  const [edges, setEdges] = useEdgesState(generatedEdges);

  const tableNamesForReports = useMemo(() => {
    const names = nodes
      .map((node) => node?.data?.tableName)
      .filter((name) => !!name && name.trim().length > 0);

    return Array.from(new Set(names)).sort();
  }, [nodes]);

  const tableNamesForReportsKey = useMemo(
    () => tableNamesForReports.join("|"),
    [tableNamesForReports]
  );

  // Update nodes and edges when API data changes
  useEffect(() => {
    setNodes(generatedNodes);
    setEdges(generatedEdges);
  }, [generatedNodes, generatedEdges, setNodes, setEdges]);

  useEffect(() => {
    if (sourceType === "uc_gcp" || !tableNamesForReports.length) {
      setReportsByTable({});
      return;
    }

    let cancelled = false;

    const fetchReportsByTables = async () => {
      try {
        const params = new URLSearchParams();
        tableNamesForReports.forEach((name) => {
          params.append("table_name", name);
        });
        params.append("source_type", sourceType || "uc");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lineage/reports-by-tables?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rows = await response.json();
        if (cancelled) {
          return;
        }

        const grouped = {};
        if (Array.isArray(rows)) {
          rows.forEach((row) => {
            const table = row?.source_full_name;
            if (!table) {
              return;
            }

            if (!grouped[table]) {
              grouped[table] = [];
            }

            const exists = grouped[table].some(
              (item) =>
                (item.report_id || "") === (row.report_id || "") &&
                (item.report_name || "") === (row.report_name || "")
            );

            if (!exists) {
              grouped[table].push({
                report_id: row.report_id,
                report_name: row.report_name,
              });
            }
          });
        }

        setReportsByTable(grouped);
      } catch (err) {
        if (!cancelled) {
          setReportsByTable({});
        }
      }
    };

    fetchReportsByTables();

    return () => {
      cancelled = true;
    };
  }, [sourceType, tableNamesForReportsKey]);

  const onNodesChange = useCallback(
    (changes) =>
      setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot)),
    []
  );
  const onEdgesChange = useCallback(
    (changes) =>
      setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot)),
    []
  );

  const onConnect = useCallback(
    async (params) => {
      // Add edge to React Flow first
      const newEdges = addEdge(params, edges);
      setEdges(newEdges);

      // Try to save as custom lineage when at least one side is a custom table.
      // For non-custom nodes, fall back to tableName from node data.
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      if (sourceNode && targetNode) {
        const sourceFullName = nodeToFullName[params.source] || sourceNode?.data?.tableName;
        const targetFullName = nodeToFullName[params.target] || targetNode?.data?.tableName;
        const sourceIsCustom = !!nodeToFullName[params.source] || !!sourceNode?.data?.isCustom;
        const targetIsCustom = !!nodeToFullName[params.target] || !!targetNode?.data?.isCustom;

        // Persist custom lineage if at least one node is custom and both names are resolvable.
        if ((sourceIsCustom || targetIsCustom) && sourceFullName && targetFullName) {
          try {
            setSavingEdgeId(params.source + '-' + params.target);
            const response = await customLineageService.createCustomLineage({
              source_full_name: sourceFullName,
              target_full_name: targetFullName,
              description: `Connection from ${sourceNode.data.tableName} to ${targetNode.data.tableName}`,
              column_mappings: []
            });

            // Store edge to lineage mapping
            const newEdgeId = `${params.source}-${params.target}`;
            setEdgeToLineageId(prev => ({
              ...prev,
              [newEdgeId]: response.id
            }));

            toast.success(`Lineage connected: ${sourceNode.data.tableName} → ${targetNode.data.tableName}`);
          } catch (error) {
            console.error('Error saving lineage connection:', error);
            toast.error('Failed to save lineage connection. Please try again.');
          } finally {
            setSavingEdgeId(null);
          }
        }
      }
    },
    [edges, setEdges, nodes, nodeToFullName]
  );

  // Edit Table functionality (defined before onNodeClick to avoid reference error)
  const handleEditTable = useCallback((nodeId) => {
    setModalMode('edit');
    setEditingNode(nodeId);
    setIsModalOpen(true);
  }, []);

  const onNodeClick = useCallback(
    (event, node) => {
      setSelectedNode(selectedNode === node.id ? null : node.id);
    },
    [selectedNode]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Add Table functionality
  const handleAddTable = useCallback(() => {
    setModalMode('add');
    setEditingNode(null);
    setIsModalOpen(true);
  }, []);

  // Modal submit handler - now saves to API
  const handleModalSubmit = useCallback(async (formData) => {
    const isEditMode = modalMode === 'edit' && !!editingNode;
    const targetNodeId = isEditMode ? editingNode : nodeCounter.toString();

    setSavingTableNodeId(targetNodeId);
    try {
      const currentFullName = isEditMode ? nodeToFullName[editingNode] : null;

      // Build table data
      const tableData = {
        name: formData.tableName,
        description: formData.description,
        schema_name: formData.schemaName || null,
        database_name: formData.databaseName || null,
        source_system: formData.sourceSystem || null,
        owner: formData.owner || "User",
        columns: formData.columns.map(col => ({
          name: col.name,
          type: col.dataType,
          description: col.description || null,
        }))
      };

      let fullName = currentFullName;
      let response;

      if (isEditMode && currentFullName) {
        // Update existing custom table
        response = await customLineageService.updateCustomTable(currentFullName, {
          ...tableData,
          full_name: currentFullName
        });
        fullName = response.full_name;
        toast.success(`Table "${formData.tableName}" updated successfully!`);
      } else {
        // Create a new custom table
        const safeTableName = formData.tableName.trim().toLowerCase().replace(/\s+/g, '_');
        const safeSchemaName = (formData.schemaName || '').trim().toLowerCase().replace(/\s+/g, '_');
        const safeDatabaseName = (formData.databaseName || '').trim().toLowerCase().replace(/\s+/g, '_');
        const defaultFullName = safeDatabaseName
          ? `${safeDatabaseName}.${safeSchemaName}.${safeTableName}`
          : `${safeSchemaName}.${safeTableName}`;
        response = await customLineageService.createCustomTable({
          ...tableData,
          full_name: defaultFullName
        });
        fullName = response.full_name;
        toast.success(`Table "${formData.tableName}" created successfully!`);
      }

      // Save node->full_name mapping for both add and edit flows
      setNodeToFullName(prev => ({
        ...prev,
        [targetNodeId]: fullName
      }));

      if (isEditMode) {
        // Update existing node UI
        setNodes((currentNodes) =>
          currentNodes.map((node) =>
            node.id === targetNodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    tableName: formData.tableName,
                    description: formData.description,
                    schemaName: formData.schemaName || "",
                    databaseName: formData.databaseName || "",
                    sourceSystem: formData.sourceSystem || "",
                    owner: formData.owner || "User",
                    allColumns: formData.columns.map(col => ({
                      name: col.name,
                      type: col.dataType,
                      description: col.description || "",
                    })),
                    visibleColumns: formData.columns.slice(0, 3).map(col => ({
                      name: col.name,
                      type: col.dataType
                    })),
                    showMoreText: formData.columns.length > 3 ? `Show ${formData.columns.length - 3} more columns` : null,
                    isEmpty: false,
                    isCustom: true
                  }
                }
              : node
          )
        );
      } else {
        // Add newly created node to canvas only after API creation succeeds
        setNodeCounter(prev => prev + 1);
        const newNode = {
          id: targetNodeId,
          type: "tableNode",
          position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
          data: {
            tableName: formData.tableName,
            description: formData.description,
            schemaName: formData.schemaName || "",
            databaseName: formData.databaseName || "",
            sourceSystem: formData.sourceSystem || "",
            owner: formData.owner || "User",
            allColumns: formData.columns.map(col => ({
              name: col.name,
              type: col.dataType,
              description: col.description || "",
            })),
            visibleColumns: formData.columns.slice(0, 3).map(col => ({
              name: col.name,
              type: col.dataType
            })),
            showMoreText: formData.columns.length > 3 ? `Show ${formData.columns.length - 3} more columns` : null,
            isEmpty: false,
            isCustom: true,
            isPrimaryNode: false
          }
        };
        setNodes((currentNodes) => [...currentNodes, newNode]);
      }

      setIsModalOpen(false);
      setEditingNode(null);
      setModalMode('add');
    } catch (error) {
      console.error('Error saving table:', error);
      toast.error(error.response?.data?.detail || 'Failed to save table. Please try again.');
    } finally {
      setSavingTableNodeId(null);
    }
  }, [modalMode, editingNode, nodeCounter, nodeToFullName, setNodes]);

  // Modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingNode(null);
    setModalMode('add');
  }, []);

  // Delete node functionality - now deletes from API if custom
  const handleDeleteNode = useCallback(async (nodeIdParam = null) => {
    const targetNodeId = nodeIdParam || selectedNode;
    if (targetNodeId) {
      const node = nodes.find(n => n.id === targetNodeId);
      const nodeName = node?.data?.tableName || 'this node';
      const fullName = nodeToFullName[targetNodeId];

      setSavingTableNodeId(targetNodeId);
      try {
        // If it's a custom table, delete from API
        if (fullName) {
          await customLineageService.deleteCustomTable(fullName);
          toast.success(`Table "${nodeName}" deleted successfully!`);
          
          // Clear from mapping
          setNodeToFullName(prev => {
            const updated = { ...prev };
            delete updated[targetNodeId];
            return updated;
          });
        }

        // Remove from UI (works for both custom and Databricks nodes)
        setNodes((currentNodes) => currentNodes.filter(node => node.id !== targetNodeId));
        
        // Also remove related edges and their lineage mappings
        const relatedEdges = edges.filter(edge => 
          edge.source === targetNodeId || edge.target === targetNodeId
        );
        
        // Delete related custom lineage edges
        for (const edge of relatedEdges) {
          const edgeKey = `${edge.source}-${edge.target}`;
          const lineageId = edgeToLineageId[edgeKey];
          if (lineageId) {
            try {
              await customLineageService.deleteCustomLineage(lineageId);
              setEdgeToLineageId(prev => {
                const updated = { ...prev };
                delete updated[edgeKey];
                return updated;
              });
            } catch (error) {
              console.error('Error deleting lineage edge:', error);
            }
          }
        }

        setEdges((currentEdges) => currentEdges.filter(edge => 
          edge.source !== targetNodeId && edge.target !== targetNodeId
        ));
        setSelectedNode(null);
      } catch (error) {
        console.error('Error deleting table:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete table. Please try again.');
      } finally {
        setSavingTableNodeId(null);
      }
    }
  }, [selectedNode, nodes, edges, nodeToFullName, edgeToLineageId, setNodes, setEdges]);

  // Expand node functionality - fetch children/parents for a specific node
  const handleExpandNode = useCallback(async (nodeId, tableName) => {
    setExpandingNodeId(nodeId); // Set loading state for this specific node
    
    try {
      const expandApiUrl = `${process.env.REACT_APP_API_URL}/lineage?table_name=${filePath}&source_type=${sourceType}`;
      const response = await fetch(expandApiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Get the current node to use as parent for positioning
      const currentNode = nodes.find(n => n.id === nodeId);
      if (!currentNode) return;

      // Create a temporary node ID map to track existing nodes by table name
      const existingNodesByTableName = new Map();
      nodes.forEach(node => {
        if (node.data.tableName) {
          existingNodesByTableName.set(node.data.tableName, node.id);
        }
      });

      // Process the API response manually to avoid duplicate node creation
      const nodesToAdd = [];
      const edgesToAdd = [];
      let tempNodeCounter = 10000; // Start from high number to avoid conflicts

      // Helper function to get or create node ID
      const getOrCreateNodeId = (tableName) => {
        if (existingNodesByTableName.has(tableName)) {
          return existingNodesByTableName.get(tableName);
        }
        const newId = tempNodeCounter.toString();
        tempNodeCounter++;
        existingNodesByTableName.set(tableName, newId);
        return newId;
      };

      // Helper function to create node from metadata
      const createNodeFromMetadataForExpansion = (metadata, tableName, position, columns = null) => {
        const nodeId = getOrCreateNodeId(tableName);
        
        // Use columns parameter first, then fall back to metadata.columns
        const columnsToUse = columns || (metadata && metadata.columns) || [];
        
        // Handle empty arrays - return early with empty columns
        if (!Array.isArray(columnsToUse) || columnsToUse.length === 0) {
          return {
            id: nodeId,
            type: "tableNode",
            position,
            data: {
              tableName: tableName,
              owner: (metadata && metadata.owner) || "Unknown",
              description: (metadata && metadata.description) || "",
              schemaName: (metadata && metadata.schema_name) || "",
              databaseName: (metadata && metadata.database_name) || "",
              sourceSystem: (metadata && metadata.source_system) || "",
              visibleColumns: [],
              allColumns: [],
              showMoreText: null,
              isPrimaryNode: false,
            },
          };
        }
        
        // Handle both old format (objects) and new format (strings)
        const allColumns = columnsToUse.map((col) => {
          if (typeof col === 'string') {
            return {
              name: col,
              type: "string",
              description: "",
            };
          } else {
            return {
              name: col.name || col.column_name || "",
              type: col.type || col.column_type || col.type_name || col.type_text || "unknown",
              description: col.description || col.column_description || "",
            };
          }
        });

        const visibleColumns = allColumns.slice(0, 3);
        const remainingCount = allColumns.length - 3;
        const showMoreText = remainingCount > 0 ? `Show ${remainingCount} more columns` : null;

        return {
          id: nodeId,
          type: "tableNode",
          position,
          data: {
            tableName: tableName,
            owner: (metadata && metadata.owner) || "Unknown",
            description: (metadata && metadata.description) || "",
            schemaName: (metadata && metadata.schema_name) || "",
            databaseName: (metadata && metadata.database_name) || "",
            sourceSystem: (metadata && metadata.source_system) || "",
            visibleColumns,
            allColumns,
            showMoreText,
            isPrimaryNode: false,
          },
        };
      };

      // Process upstreams (parents of the clicked node)
      if (data.upstreams && Array.isArray(data.upstreams)) {
        data.upstreams.forEach((upstream, index) => {
          const parentTableName = upstream.child;
          if (!parentTableName) return;

          const parentNodeId = getOrCreateNodeId(parentTableName);
          
          // Only create node if it doesn't exist
          if (!existingNodesByTableName.has(parentTableName) || 
              !nodes.find(n => n.data.tableName === parentTableName)) {
            const position = {
              x: Math.max(0, currentNode.position.x - 600),
              y: currentNode.position.y + (index - data.upstreams.length / 2) * 250,
            };
            const node = createNodeFromMetadataForExpansion(
              upstream.metadata, 
              parentTableName, 
              position, 
              upstream.columns // Pass columns from new API structure
            );
            nodesToAdd.push(node);
          }

          // Create edge from parent to clicked node
          const edgeId = `e${parentNodeId}-${nodeId}`;
          if (!edges.find(e => e.id === edgeId)) {
            edgesToAdd.push({
              id: edgeId,
              source: parentNodeId,
              target: nodeId,
              type: "bezier",
              animated: false,
              style: {
                stroke: '#374151',
                strokeWidth: 3,
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#374151',
                width: 20,
                height: 20
              },
            });
          }
        });
      }

      // Process downstreams (children of the clicked node)
      if (data.downstreams && Array.isArray(data.downstreams)) {
        data.downstreams.forEach((downstream, index) => {
          const childTableName = downstream.parent;
          if (!childTableName) return;

          const childNodeId = getOrCreateNodeId(childTableName);
          
          // Only create node if it doesn't exist
          if (!existingNodesByTableName.has(childTableName) || 
              !nodes.find(n => n.data.tableName === childTableName)) {
            const position = {
              x: currentNode.position.x + 600,
              y: currentNode.position.y + (index - data.downstreams.length / 2) * 250,
            };
            const node = createNodeFromMetadataForExpansion(
              downstream.metadata, 
              childTableName, 
              position,
              downstream.columns // Pass columns from new API structure
            );
            nodesToAdd.push(node);
          }

          // Create edge from clicked node to child
          const edgeId = `e${nodeId}-${childNodeId}`;
          if (!edges.find(e => e.id === edgeId)) {
            edgesToAdd.push({
              id: edgeId,
              source: nodeId,
              target: childNodeId,
              type: "bezier",
              animated: false,
              style: {
                stroke: '#374151',
                strokeWidth: 3,
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#374151',
                width: 20,
                height: 20
              },
            });
          }
        });
      }

      // Add new nodes and edges to the graph
      if (nodesToAdd.length > 0) {
        setNodes((currentNodes) => [...currentNodes, ...nodesToAdd]);
      }
      if (edgesToAdd.length > 0) {
        setEdges((currentEdges) => [...currentEdges, ...edgesToAdd]);
      }

    } catch (err) {
      console.error('Failed to expand node:', err.message);
      // You can add a toast notification here if needed
    } finally {
      setExpandingNodeId(null); // Clear loading state
    }
  }, [nodes, edges, setNodes, setEdges]);

  // Column Lineage functionality - fetch and display column-level lineage
  const handleColumnLineage = useCallback(async (nodeId, tableName) => {
    // Check if column lineage is already displayed for this node
    if (columnLineageData[nodeId]) {
      // Get all edges that belong to this node
      const edgesToRemove = columnLineageEdges.filter(edge => edge.id.startsWith(`col-${nodeId}-`));
      
      // Collect all affected node IDs from the edges being removed
      const affectedNodeIds = new Set([nodeId]);
      edgesToRemove.forEach(edge => {
        affectedNodeIds.add(edge.source);
        affectedNodeIds.add(edge.target);
      });
      
      // Remove column lineage for this node
      setColumnLineageData((prevData) => {
        const newData = { ...prevData };
        delete newData[nodeId];
        return newData;
      });
      
      // Remove column lineage edges for this node
      setColumnLineageEdges((prevEdges) => 
        prevEdges.filter(edge => !edge.id.startsWith(`col-${nodeId}-`))
      );
      
      // Check if any of the affected nodes still have column lineage from other nodes
      const remainingAffectedNodes = new Set();
      columnLineageEdges.forEach(edge => {
        // Skip edges that will be removed
        if (!edge.id.startsWith(`col-${nodeId}-`)) {
          if (affectedNodeIds.has(edge.source)) {
            remainingAffectedNodes.add(edge.source);
          }
          if (affectedNodeIds.has(edge.target)) {
            remainingAffectedNodes.add(edge.target);
          }
        }
      });
      
      setNodes((currentNodes) => 
        currentNodes.map(node => {
          if (affectedNodeIds.has(node.id) && !remainingAffectedNodes.has(node.id)) {
            return { ...node, data: { ...node.data, hasColumnLineage: false } };
          }
          return node;
        })
      );
      
      return;
    }

    setColumnLineageLoadingNodeId(nodeId); // Set loading state

    try {
      const columnLineageApiUrl = `${process.env.REACT_APP_API_URL}/column-lineage?table_name=${tableName}&source_type=${sourceType}`;
      const response = await fetch(columnLineageApiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store the column lineage data
      setColumnLineageData((prevData) => ({
        ...prevData,
        [nodeId]: data,
      }));

      // Create column lineage edges
      const newColumnEdges = [];
      const nodesByTableName = new Map();
      const affectedNodeIds = new Set([nodeId]); // Start with clicked node
      
      // Helper function to normalize table names for matching
      const normalizeTableName = (tableName) => {
        if (!tableName) return '';
        // Remove any special characters and convert to lowercase for comparison
        return tableName.toLowerCase().replace(/[^a-z0-9._]/g, '');
      };
      
      // Helper function to find node by table name (flexible matching)
      const findNodeByTableName = (tableName) => {
        if (!tableName) return null;
        const normalized = normalizeTableName(tableName);
        
        // First try exact match
        for (const node of nodes) {
          if (node.data.tableName && normalizeTableName(node.data.tableName) === normalized) {
            return node.id;
          }
        }
        
        // Try partial match (table name contains or is contained in node name)
        for (const node of nodes) {
          const nodeTableName = node.data.tableName;
          if (nodeTableName) {
            const nodeNormalized = normalizeTableName(nodeTableName);
            // Check if either contains the other (for flexible matching)
            if (normalized.includes(nodeNormalized) || nodeNormalized.includes(normalized)) {
              return node.id;
            }
            // Also try matching the last part (table name after last dot)
            const tableLastPart = normalized.split('.').pop();
            const nodeLastPart = nodeNormalized.split('.').pop();
            if (tableLastPart && nodeLastPart && tableLastPart === nodeLastPart) {
              return node.id;
            }
          }
        }
        
        return null;
      };
      
      // Build a map of table names to node IDs (with flexible matching)
      nodes.forEach(node => {
        if (node.data.tableName) {
          const normalized = normalizeTableName(node.data.tableName);
          nodesByTableName.set(normalized, node.id);
          // Also store by last part of table name
          const lastPart = normalized.split('.').pop();
          if (lastPart) {
            nodesByTableName.set(lastPart, node.id);
          }
        }
      });

      // Process upstream lineage (parent columns -> current node columns)
      if (data['upstream-lineage'] && Array.isArray(data['upstream-lineage'])) {
        data['upstream-lineage'].forEach((lineage, index) => {
          const sourceTableName = lineage.source_lineage;
          const targetTableName = lineage.target_lineage;
          
          const sourceNodeId = findNodeByTableName(sourceTableName);
          const targetNodeId = findNodeByTableName(targetTableName);

          // Add both source and target nodes to affected nodes
          if (sourceNodeId) {
            affectedNodeIds.add(sourceNodeId);
          } else {
            console.warn(`⚠️ Source node not found for table: ${sourceTableName}`);
          }
          
          if (targetNodeId) {
            affectedNodeIds.add(targetNodeId);
          } else {
            console.warn(`⚠️ Target node not found for table: ${targetTableName}`);
          }

          // Only create edge if both nodes exist
          if (sourceNodeId && targetNodeId) {
            newColumnEdges.push({
              id: `col-${nodeId}-up-${index}`,
              source: sourceNodeId,
              target: targetNodeId,
              sourceHandle: `col-${lineage.source_column_name}`,
              targetHandle: `col-${lineage.target_column_name}`,
              type: "bezier",
              animated: false,
              style: { 
                stroke: '#f59e0b', 
                strokeWidth: 2,
                strokeDasharray: '0'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#f59e0b',
                width: 20,
                height: 20
              },
              data: {
                isColumnLineage: true,
                sourceColumn: lineage.source_column_name,
                targetColumn: lineage.target_column_name,
                nodeId: nodeId,
                edgeType: 'upstream'
              },
            });
          }
        });
      }

      // Process downstream lineage (current node columns -> child columns)
      if (data['downstream-lineage'] && Array.isArray(data['downstream-lineage'])) {
        data['downstream-lineage'].forEach((lineage, index) => {
          const sourceTableName = lineage.source_lineage;
          const targetTableName = lineage.target_lineage;
          
          const sourceNodeId = findNodeByTableName(sourceTableName);
          const targetNodeId = findNodeByTableName(targetTableName);

          // Add both source and target nodes to affected nodes
          if (sourceNodeId) {
            affectedNodeIds.add(sourceNodeId);
          } else {
            console.warn(`⚠️ Source node not found for table: ${sourceTableName}`);
          }
          
          if (targetNodeId) {
            affectedNodeIds.add(targetNodeId);
          } else {
            console.warn(`⚠️ Target node not found for table: ${targetTableName}`);
          }

          // Only create edge if both nodes exist
          if (sourceNodeId && targetNodeId) {
            newColumnEdges.push({
              id: `col-${nodeId}-down-${index}`,
              source: sourceNodeId,
              target: targetNodeId,
              sourceHandle: `col-${lineage.source_column_name}`,
              targetHandle: `col-${lineage.target_column_name}`,
              type: "bezier",
              animated: false,
              style: { 
                stroke: '#10b981', 
                strokeWidth: 2,
                strokeDasharray: '0'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#10b981',
                width: 20,
                height: 20
              },
              data: {
                isColumnLineage: true,
                sourceColumn: lineage.source_column_name,
                targetColumn: lineage.target_column_name,
                nodeId: nodeId,
                edgeType: 'downstream'
              },
            });
          }
        });
      }
      
      setColumnLineageEdges((prevEdges) => [...prevEdges, ...newColumnEdges]);

      // Immediately update nodes to set hasColumnLineage flag
      setNodes((currentNodes) => {
        const updatedNodes = currentNodes.map(node => {
          if (affectedNodeIds.has(node.id)) {
            return { 
              ...node, 
              data: { 
                ...node.data, 
                hasColumnLineage: true 
              } 
            };
          }
          return node;
        });
        return updatedNodes;
      });

    } catch (err) {
      console.error('Failed to fetch column lineage:', err.message);
      alert(`Failed to fetch column lineage: ${err.message}`);
    } finally {
      setColumnLineageLoadingNodeId(null); // Clear loading state
    }
  }, [nodes, columnLineageData, setColumnLineageEdges, setNodes]);

  // Handle column click to highlight related edges
  const handleColumnClick = useCallback((nodeId, columnName, tableName) => {
    // If clicking the same column, deselect it
    if (highlightedColumn && 
        highlightedColumn.nodeId === nodeId && 
        highlightedColumn.columnName === columnName) {
      setHighlightedColumn(null);
    } else {
      // Find all related columns through column lineage edges
      const relatedColumns = [];
      const processedPairs = new Set(); // To avoid duplicates
      
      columnLineageEdges.forEach(edge => {
        // Check if this edge involves the clicked column
        if (edge.data && edge.data.isColumnLineage) {
          // If the clicked column is the source
          if (edge.source === nodeId && edge.data.sourceColumn === columnName) {
            const pairKey = `${edge.target}-${edge.data.targetColumn}`;
            if (!processedPairs.has(pairKey)) {
              relatedColumns.push({
                nodeId: edge.target,
                columnName: edge.data.targetColumn
              });
              processedPairs.add(pairKey);
            }
          }
          // If the clicked column is the target
          else if (edge.target === nodeId && edge.data.targetColumn === columnName) {
            const pairKey = `${edge.source}-${edge.data.sourceColumn}`;
            if (!processedPairs.has(pairKey)) {
              relatedColumns.push({
                nodeId: edge.source,
                columnName: edge.data.sourceColumn
              });
              processedPairs.add(pairKey);
            }
          }
        }
      });
      
      // Highlight the clicked column and all related columns
      setHighlightedColumn({ nodeId, columnName, tableName, relatedColumns });
    }
  }, [highlightedColumn, columnLineageEdges]);

  // Update nodes to include selected state and expand handler
  const nodesWithSelection = useMemo(() => {
    // Collect all connected columns based on actual column lineage edges
    const allConnectedColumns = new Map(); // nodeId -> Set of connected columns
    
    // Process all column lineage edges to find connected columns
    columnLineageEdges.forEach(edge => {
      if (edge.data && edge.data.isColumnLineage) {
        // Add source column to source node
        if (!allConnectedColumns.has(edge.source)) {
          allConnectedColumns.set(edge.source, new Set());
        }
        allConnectedColumns.get(edge.source).add(edge.data.sourceColumn);
        
        // Add target column to target node
        if (!allConnectedColumns.has(edge.target)) {
          allConnectedColumns.set(edge.target, new Set());
        }
        allConnectedColumns.get(edge.target).add(edge.data.targetColumn);
      }
    });

    return nodes.map((node) => {
      // Get connected columns for this specific node
      const nodeConnectedColumns = allConnectedColumns.get(node.id) || new Set();

      return {
      ...node,
      data: {
        ...node.data,
        selected: selectedNode === node.id,
          onEditNode: handleEditTable,
          onDeleteNode: handleDeleteNode,
          isDeleting: savingTableNodeId === node.id,
          onExpandNode: handleExpandNode,
          isExpanding: expandingNodeId === node.id,
          onColumnLineage: handleColumnLineage,
          isColumnLineageLoading: columnLineageLoadingNodeId === node.id,
          hasColumnLineage: !!columnLineageData[node.id],
          connectedColumns: Array.from(nodeConnectedColumns), // Pass all connected columns to node
          onColumnClick: handleColumnClick,
          highlightedColumn: highlightedColumn,
        },
      };
    });
  }, [nodes, selectedNode, handleEditTable, handleDeleteNode, savingTableNodeId, handleExpandNode, expandingNodeId, handleColumnLineage, columnLineageLoadingNodeId, columnLineageData, columnLineageEdges, handleColumnClick, highlightedColumn, reportsByTable]);

  const reportGraph = useMemo(() => {
    const tableNodeByName = new Map();
    nodesWithSelection.forEach((node) => {
      const tableName = node?.data?.tableName;
      if (tableName) {
        tableNodeByName.set(tableName, node);
      }
    });

    const reportToTables = new Map();

    Object.entries(reportsByTable || {}).forEach(([tableName, reports]) => {
      const tableNode = tableNodeByName.get(tableName);
      if (!tableNode || !Array.isArray(reports)) {
        return;
      }

      reports.forEach((report) => {
        const reportKeyRaw = report?.report_id || report?.report_name;
        if (!reportKeyRaw) {
          return;
        }

        const reportKey = String(reportKeyRaw);
        const reportLabel = report?.report_name || String(reportKeyRaw);
        if (!reportToTables.has(reportKey)) {
          reportToTables.set(reportKey, {
            label: reportLabel,
            tableNodes: [],
          });
        }

        reportToTables.get(reportKey).tableNodes.push(tableNode);
      });
    });

    const reportNodes = [];
    const reportEdges = [];
    const usedY = [];

    reportToTables.forEach((value, reportKey) => {
      const related = value.tableNodes;
      if (!related.length) {
        return;
      }

      const xBase = Math.max(...related.map((n) => n.position.x)) + 480;
      let yBase = related.reduce((sum, n) => sum + n.position.y, 0) / related.length;

      // Prevent overlapping report nodes when averages are close.
      while (usedY.some((y) => Math.abs(y - yBase) < 110)) {
        yBase += 120;
      }
      usedY.push(yBase);

      const reportNodeId = `report-node-${toSafeGraphId(reportKey)}`;
      reportNodes.push({
        id: reportNodeId,
        type: "reportNode",
        position: { x: xBase, y: yBase },
        data: {
          label: value.label,
        },
      });

      related.forEach((tableNode) => {
        const edgeId = `table-report-${tableNode.id}-${reportNodeId}`;
        reportEdges.push({
          id: edgeId,
          source: tableNode.id,
          target: reportNodeId,
          sourceHandle: "header-right",
          targetHandle: "header-left",
          type: "bezier",
          animated: false,
          style: {
            stroke: "#0f766e",
            strokeWidth: 2,
          },
          markerEnd: {
            type: "arrowclosed",
            color: "#0f766e",
            width: 18,
            height: 18,
          },
        });
      });
    });

    return {
      reportNodes,
      reportEdges,
    };
  }, [nodesWithSelection, reportsByTable]);

  const allNodes = useMemo(
    () => [...nodesWithSelection, ...reportGraph.reportNodes],
    [nodesWithSelection, reportGraph.reportNodes]
  );

  // Merge regular edges with column lineage edges and apply highlighting
  const allEdges = useMemo(() => {
    const merged = [...edges, ...reportGraph.reportEdges, ...columnLineageEdges].map(edge => {
      // Check if this edge should be highlighted based on the clicked column
      if (highlightedColumn && edge.data && edge.data.isColumnLineage) {
        // Check if edge connects to the clicked column
        const isDirectlyRelated = 
          (edge.data.sourceColumn === highlightedColumn.columnName || 
           edge.data.targetColumn === highlightedColumn.columnName) &&
          (edge.source === highlightedColumn.nodeId || edge.target === highlightedColumn.nodeId);
        
        // Check if edge connects to any related column
        const isRelatedToRelatedColumn = highlightedColumn.relatedColumns && 
          highlightedColumn.relatedColumns.some(rc => {
            return (
              (edge.source === rc.nodeId && edge.data.sourceColumn === rc.columnName) ||
              (edge.target === rc.nodeId && edge.data.targetColumn === rc.columnName)
            );
          });
        
        if (isDirectlyRelated || isRelatedToRelatedColumn) {
          // Highlight the edge
          return {
            ...edge,
            animated: true,
            style: {
              ...edge.style,
              stroke: '#8b5cf6', // Purple color for highlighted edges
              strokeWidth: 4,
            },
            markerEnd: {
              ...edge.markerEnd,
              color: '#8b5cf6',
              width: 25,
              height: 25
            },
            className: 'column-edge-highlighted'
          };
        }
      }
      return edge;
    });
    return merged;
  }, [edges, reportGraph.reportEdges, columnLineageEdges, highlightedColumn]);

  // Loading component
  const LoadingComponent = () => (
 
      <div className="flex items-center justify-center h-[300px]">
                  <div className="flex flex-col items-center space-y-3">
                    <img src={loader} alt="Loading..." className="h-30" />
                    <span className="text-gray-700 text-[20px] text-center">
                     Loading lineage data...
                    </span>
                  </div>
                </div>
  );

  // Error component
  const ErrorComponent = () => (
    <div className="error-container">
      <div className="error-icon">⚠️</div>
      <div className="error-title">Failed to load data</div>
      <div className="error-message">
        API Error: {error}
        <br />
        {/* <span className="fallback-notice">
          Using fallback data from DummyData.js
        </span> */}
      </div>
      <button className="retry-button" onClick={fetchLineageData}>
        Retry API Call
      </button>
    </div>
  );

  // Show loading state
  if (loading) {
    return (
      <div className="databricks-lineage-container">
        <LoadingComponent />
      </div>
    );
  }

  // Show error state (but still render the graph with fallback data)
  if (error) {
    return (
      <div className="databricks-lineage-container">
        <div className="error-banner">
          <ErrorComponent />
        </div>
        
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Add Table Button */}
        <div className="viewlineage-buttons-container">
          <button 
            className="viewlineage-add-table-btn"
            onClick={handleAddTable}
          >
            + Add Table
          </button>
        </div>

        <ReactFlow
        nodes={allNodes}
        edges={allEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        className="lineage-flow"
        fitViewOptions={{ padding: 0.1 }}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        minZoom={0.1}
        maxZoom={4}
        >
          <Controls
            position="bottom-right" // or "top-left", "top-right", "bottom-left"
            showZoom={true}
            showFitView={true}
            showInteractive={true}
          />
          <MiniMap
            position="bottom-left" // or other positions
          />
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="hsl(220 17% 17%)"
          />
        </ReactFlow>

        {/* Table Details Modal */}
        <TableDetailsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          editingNode={editingNode}
          nodes={nodes}
          mode={modalMode}
        />

        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </div>
    </ReactFlowProvider>
  );
};

export default ViewLineage;





